/* eslint-disable no-console */
// Fuente ÚNICA del cálculo de cumpleaños del banner del Home (Fase 2). El dato vive en
// `usuarios.cumple_mes`/`cumple_dia` (solo mes y día, SIN año — Fase 1; ver ficha usuarios.md).
// Acá se define: qué universo ve cada rol, el filtro por país, la ventana móvil de 7 días
// contando hoy (sobre la fecha LOCAL del navegador) y el saludo del 29/2 en años no bisiestos.
// PROHIBIDO el fallo silencioso (10.177): si la query falla se loguea y se devuelve el estado
// vacío con `error: true` — el banner NUNCA desaparece sin dejar rastro.
import { resolverEntidadUsuario } from "./resolverEntidadUsuario";
import { client } from "./supabase";

// Universo (roles de contacto) que ve cada rol que MIRA el banner. Matriz CERRADA definida por
// Ignacio: cualquier rol fuera de esta lista NO ve el banner (el componente hace return null).
export const universoRolesPara = (rolViewer) => {
  switch (rolViewer) {
    case "gerencia":
    case "superadmin":
    case "board":
      return ["agencia", "cliente", "edificio"];
    case "comercial":
      return ["agencia", "cliente"];
    case "infraestructura":
      return ["edificio"];
    default:
      return null; // no ve el banner
  }
};

// `pais` del auth (formato largo) → códigos de `usuarios.pais` (10.53: la tabla usa CHI/PER/REG,
// NO Chile/Perú). Region ve Chile + Perú juntos. Uruguay JAMÁS entra (10.10 / 10.181).
export const paisCodesPara = (paisViewer) => {
  if (paisViewer === "Chile") return ["CHI"];
  if (paisViewer === "Perú") return ["PER"];
  if (paisViewer === "Region") return ["CHI", "PER"];
  return [];
};

const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

// (mes,dia) efectivos para ESTE año: el 29/2 se saluda el 28/2 en años no bisiestos.
const efectivoParaAno = (mes, dia, ano) =>
  mes === 2 && dia === 29 && !isLeap(ano) ? { mes: 2, dia: 28 } : { mes, dia };

// Ventana móvil de 7 días CONTANDO HOY (offset 0..6) sobre la fecha local. Map "mes-dia" → offset
// (0 = hoy). Cruza fin de mes/año por construcción (28 dic ve el 3 ene).
const construirVentana = (hoy) => {
  const ventana = new Map();
  for (let offset = 0; offset < 7; offset++) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + offset);
    ventana.set(`${d.getMonth() + 1}-${d.getDate()}`, offset);
  }
  return ventana;
};

// "9 jun" / "22 nov" en es-CL, SIN año (el 2000 es un ancla que nunca se muestra).
export const formatDiaMes = (mes, dia) =>
  new Date(2000, mes - 1, dia).toLocaleDateString("es-CL", { day: "numeric", month: "short" });

const fetchByIds = async (tabla, columns, ids) => {
  const { data, error } = await client.from(tabla).select(columns).in("id", ids);
  if (error) throw error;
  return data || [];
};

const uniq = (arr) => [...new Set(arr)];

const edificioIdsDe = (usuario) =>
  String(usuario.edificiosOk || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number);

/**
 * Devuelve { hoy: Fila[], proximos: Fila[], error: boolean } para el rol/país que MIRA.
 * Fila = { id, nombre, entidad, mes, dia, pais }. `hoy` = cumplen hoy; `proximos` = los que
 * caen en los siguientes 6 días de la ventana (ordenados por fecha y luego nombre).
 * `hoy` inyectable para tests / control (default = ahora, hora local).
 */
export const getCumpleanos = async ({ rolViewer, paisViewer, hoy = new Date() } = {}) => {
  const roles = universoRolesPara(rolViewer);
  const paisCodes = paisCodesPara(paisViewer);
  const vacio = { hoy: [], proximos: [], error: false };
  if (!roles || paisCodes.length === 0) return vacio;

  try {
    // Query chica por construcción (4 filtros duros). Si el universo creciera, paginar con
    // `.range()` (10.167) — hoy el resultado es de decenas de filas como mucho.
    const { data, error } = await client
      .from("usuarios")
      .select("id, nombre, apellido, rol, agencia, cliente_id, edificiosOk, cumple_mes, cumple_dia, pais")
      .not("cumple_mes", "is", null)
      .not("habilitado", "is", false)
      .in("rol", roles)
      .in("pais", paisCodes);
    if (error) throw error;

    const ano = hoy.getFullYear();
    const ventana = construirVentana(hoy);

    // Quedarse con los que caen en la ventana, ya con el (mes,dia) efectivo del año (29/2→28/2).
    const enVentana = [];
    for (const u of data || []) {
      const { mes, dia } = efectivoParaAno(u.cumple_mes, u.cumple_dia, ano);
      const offset = ventana.get(`${mes}-${dia}`);
      if (offset === undefined) continue;
      enVentana.push({ ...u, offset, mesDisplay: mes, diaDisplay: dia });
    }
    if (enVentana.length === 0) return vacio;

    // Lookups SOLO de los ids que se van a mostrar, resueltos por ID (10.7).
    const clienteIds = uniq(
      enVentana
        .filter((u) => u.rol === "cliente")
        .map((u) => u.cliente_id)
        .filter(Boolean)
    );
    const agenciaIds = uniq(
      enVentana
        .filter((u) => u.rol === "agencia")
        .map((u) => u.agencia)
        .filter(Boolean)
    );
    const edificioIds = uniq(enVentana.filter((u) => u.rol === "edificio").flatMap(edificioIdsDe));

    const [clientes, intermediarios, edificios] = await Promise.all([
      clienteIds.length > 0 ? fetchByIds("clientes", "id, nombre, empresa, producto", clienteIds) : [],
      agenciaIds.length > 0 ? fetchByIds("intermediarios", "id, nombre", agenciaIds) : [],
      edificioIds.length > 0 ? fetchByIds("edificios", "id, edificio", edificioIds) : [],
    ]);

    const armarFila = (u) => ({
      id: u.id,
      nombre: `${u.nombre || ""} ${u.apellido || ""}`.trim(),
      entidad: resolverEntidadUsuario(u, { clientes, intermediarios, edificios }),
      mes: u.mesDisplay,
      dia: u.diaDisplay,
      pais: u.pais,
    });

    const porNombre = (a, b) => a.nombre.localeCompare(b.nombre, "es");

    const hoyList = enVentana
      .filter((u) => u.offset === 0)
      .map(armarFila)
      .sort(porNombre);
    // Próximos: por fecha (offset) y, dentro del mismo día, por nombre. El offset viaja para el
    // sort y se descarta al final (la fila no lo expone).
    const proximosList = enVentana
      .filter((u) => u.offset > 0)
      .map((u) => ({ ...armarFila(u), offset: u.offset }))
      .sort((a, b) => a.offset - b.offset || a.nombre.localeCompare(b.nombre, "es"))
      // eslint-disable-next-line no-unused-vars
      .map(({ offset, ...fila }) => fila);

    return { hoy: hoyList, proximos: proximosList, error: false };
  } catch (err) {
    console.error("Error en getCumpleanos:", err);
    return { hoy: [], proximos: [], error: true };
  }
};
